import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {Apollo} from "apollo-angular";
import {pick} from "lodash";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {globalDealSettingsKeysToUpdate, GlobalDealSettingsModel, globalDealSettingsModelFactory} from "./global-deal-settings.model";
import {LeadCustomerGqlModel, LeadCustomerInfoGqlModel} from "./lead-customer-gpl.model";

export type CustomerOpportunityGroupListResponse = {
  rows: any[];
  count: number;
  matchFound?: boolean;
};

export type FleetModel = {
  fleetId?: string;
  fleetName?: string;
  endCustomerAccountId?: string;
  relatedAccountIds?: string[]
}

export function fleetModelFactory(json?: any): FleetModel {
  json = json || {};

  return {
    fleetId: json.fleetId || "",
    fleetName: json.fleetName || null,
    endCustomerAccountId: json.endCustomerAccountId || "",
    relatedAccountIds: json.relatedAccountIds || []
  } as FleetModel;
}

@Injectable()
export class GlobalDealSettingsService {
  private url: string = "/api/global-deal-settings";

  getFleetByCustomerOpportunityId(projectId: string, customerOpportunityGroupId: string): Promise<any> {
    return this.http.get<any[]>(`${this.url}/fleet-by-customer-opportunity-group`, {params: {customerOpportunityGroupId, projectId}})
      .pipe(map(array => array.map(fleet => fleetModelFactory(fleet))))
      .toPromise();
  }

  async listCustomerOpportunityGroups({
    search = "",
    globalOpportunityId = "",
  }, projectId,targetSystem): Promise<CustomerOpportunityGroupListResponse> {
    const params: any = {
      projectId: projectId ? `${projectId}` : "",
      targetSystem: targetSystem ? `${targetSystem}` : "",
    };

    if (globalOpportunityId) params.globalOpportunityId = `${globalOpportunityId}`;
    else params.search = search ? `${search}` : "";

    const returnVal = await this.http.get<CustomerOpportunityGroupListResponse>(
      `${this.url}/list-itsm-customer-opportunity-groups`,
      {params},
    )
      .pipe(map(json => ({
        rows: json.rows || [],
        count: +json.count,
        matchFound: false,
      })))
      .toPromise();

    let searchCanonical = search.trim().toLowerCase();
    returnVal.rows.forEach((it) => {
      const itCanonical = it.customerOpportunityGroupName.trim().toLowerCase();
      if (searchCanonical === itCanonical) returnVal.matchFound = true;
    });

    returnVal.rows.sort((a, b) => {
      // Exact Match
      if (a.customerOpportunityGroupName.trim() === search) return -1;
      if (b.customerOpportunityGroupName.trim() === search) return 1;

      // Exact Match with case insensitivity
      searchCanonical = search.trim().toLowerCase();
      const aCanonical = a.customerOpportunityGroupName.trim().toLowerCase();
      if (aCanonical === searchCanonical) return -1;
      const bCanonical = b.customerOpportunityGroupName.trim().toLowerCase();
      if (bCanonical === searchCanonical) return 1;

      // Standard ordering
      if (aCanonical < bCanonical) return -1;
      if (aCanonical > bCanonical) return 1;

      return 1;
    });

    return returnVal;
  }

  createItsmCustomerOpportunityGroup(
    customerOpportunityGroupName: string,
    projectId: string,
  ): Promise<GlobalDealSettingsModel> {
    return this.http.post<{customerOpportunityGroupName: string}>(
      `${this.url}/create-itsm-customer-opportunity-group`,
      {customerOpportunityGroupName, projectId},
    )
      .pipe(map(globalDealSettingsModelFactory))
      .toPromise();
  }

  setMs4ITSMLeadCustomer(projectId: string, selectedCustomerInfo) {
    return this.http.post(`${this.url}/set-ms4-lead-customer`, {projectId, selectedCustomerInfo}).toPromise();
  }

  setC2cDeployedCountry(projectId: string, c2c: string) {
    return this.http.post(`${this.url}/set-c2c-deployed-country`, { projectId, c2c }).toPromise();
  }

  getLeadCustomerStatus(projectId): Observable<LeadCustomerInfoGqlModel> {
    const query = LeadCustomerGqlModel.query;
    const variables = LeadCustomerGqlModel.variables(projectId);

    return this.apollo.subscribe({query, variables, fetchPolicy: "no-cache"})
      .pipe(LeadCustomerInfoGqlModel.mapper);
  }

  getGlobalDealSettings(projectId: string): Promise<GlobalDealSettingsModel> {
    const params = {
      projectId,
    };

    return this.http.get(`${this.url}/get-by-project`, {params})
      .pipe(map(globalDealSettingsModelFactory))
      .toPromise();
  }

  saveGlobalDealSettings(globalDealSettings: GlobalDealSettingsModel): Promise<GlobalDealSettingsModel> {
    const body = {...pick(globalDealSettings, globalDealSettingsKeysToUpdate)};
    return this.http.post<{globalDealSettings: GlobalDealSettingsModel}>(`${this.url}/save`, body)
      .pipe(map(globalDealSettingsModelFactory))
      .toPromise();
  }

  constructor(private http: HttpClient, private apollo: Apollo) {}
}
